"use client";

import { useEffect, useRef, useState } from "react";

export default function WhitelistedInfraSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section className="py-6 bg-[#0B0F14]">
      <div className="max-w-7xl mx-auto px-6">
        <div
          ref={ref}
          style={{
            background: "#11161D",
            border: "1px solid rgba(0, 230, 118, 0.25)",
            boxShadow: "0 0 32px rgba(0, 230, 118, 0.08), inset 0 0 60px rgba(0, 230, 118, 0.03)",
            borderRadius: 16,
            overflow: "hidden",
            position: "relative",
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(24px)",
            transition: "opacity 0.7s ease, transform 0.7s ease",
          }}
        >
          {/* Subtle top border glow line */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "1px",
              background: "linear-gradient(90deg, transparent, rgba(0,230,118,0.6), transparent)",
            }}
          />

          <div className="px-8 py-8 md:py-10 flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10">
            {/* Icon */}
            <div
              className="flex-shrink-0"
              style={{
                width: 52,
                height: 52,
                borderRadius: 12,
                background: "rgba(0,230,118,0.08)",
                border: "1px solid rgba(0,230,118,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 2L4 6V12C4 16.418 7.582 20.472 12 22C16.418 20.472 20 16.418 20 12V6L12 2Z"
                  stroke="#00E676"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                />
                <circle cx="12" cy="12" r="2" fill="#00E676" opacity="0.7" />
                <path
                  d="M12 10V8M12 14v2"
                  stroke="#00E676"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  opacity="0.5"
                />
                <path
                  d="M9 12H7M15 12h2"
                  stroke="#00E676"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  opacity="0.5"
                />
              </svg>
            </div>

            {/* Content */}
            <div className="flex-1">
              {/* Label */}
              <div
                className="font-mono text-[10px] uppercase tracking-[0.2em] mb-3"
                style={{ color: "rgba(0,230,118,0.6)" }}
              >
                Exclusive Whitelisted Infrastructure
              </div>

              {/* Headline */}
              <h2
                className="font-bold text-lg md:text-xl mb-1 leading-snug"
                style={{ color: "#F0F4F8", fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                Access to Exclusive{" "}
                <span
                  style={{
                    color: "#00E676",
                    textDecoration: "underline",
                    textDecorationColor: "rgba(0,230,118,0.35)",
                    textUnderlineOffset: "4px",
                    textShadow: "0 0 12px rgba(0,230,118,0.4)",
                  }}
                >
                  Whitelisted
                </span>{" "}
                Ad Accounts.
              </h2>

              {/* Subheadline */}
              <p
                className="text-sm mb-4"
                style={{ color: "rgba(0,230,118,0.55)", fontFamily: "'JetBrains Mono', monospace" }}
              >
                Integrated into the Scaled-Out infrastructure layer.
              </p>

              {/* Body */}
              <p
                className="text-sm leading-relaxed mb-2"
                style={{ color: "#6B7A8D", maxWidth: 720 }}
              >
                Through our verified infrastructure framework, partners gain access to exclusive whitelisted Meta ad accounts built for operational stability and scalable deployment. These accounts operate within structured environments designed to support high-volume advertisers without artificial spending constraints.
              </p>

              <p
                className="text-xs font-semibold mb-4"
                style={{ color: "rgba(240,244,248,0.55)", letterSpacing: "0.04em" }}
              >
                Integrated. Verified. Engineered for scale.
              </p>

              {/* Micro-bullets */}
              <div className="flex flex-wrap gap-4">
                {["Enhanced stability", "Structured deployment", "Infrastructure-level integration"]?.map(
                  (item) => (
                    <span
                      key={item}
                      className="flex items-center gap-1.5 text-[11px] font-mono"
                      style={{ color: "rgba(0,230,118,0.5)" }}
                    >
                      <span
                        style={{
                          width: 4,
                          height: 4,
                          borderRadius: "50%",
                          background: "#00E676",
                          display: "inline-block",
                          flexShrink: 0,
                        }}
                      />
                      {item}
                    </span>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
